import { useEffect, useState } from "react";
import { Loader2, Check, Zap, X, ArrowRight, Lock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { LOGO } from "@/components/site/assets";

const inputCls = "w-full bg-[#0A0A0A] border border-white/15 focus:border-white outline-none px-3 py-2.5 text-sm transition-colors";

export default function Memberships() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", sport: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/membership-plans").then((r) => setData(r.data)).catch(() => setError("Failed to load plans."))
      .finally(() => setLoading(false));
  }, []);

  const checkout = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      const { data: res } = await api.post("/membership/checkout", {
        ...form, lookup_key: selected.lookup_key, origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3"><img src={LOGO} alt="JJ Speed Factory" className="h-10 w-auto object-contain" /></a>
          <a href="/login" className="font-mono-accent text-xs tracking-[0.2em] uppercase text-white/60 hover:text-white transition-colors">Login</a>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 md:px-12 pt-16 pb-24">
        <p className="font-mono-accent text-xs tracking-[0.35em] uppercase text-white/40 mb-4">Memberships</p>
        <h1 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-6xl mb-4">Choose Your Path</h1>
        <p className="text-white/60 text-lg max-w-2xl mb-12">Every membership includes full Origin Speed Academy access — training, performance tracking, coach feedback and Speed Points.</p>

        {loading ? <div className="flex justify-center py-24 text-white/40"><Loader2 className="animate-spin" /></div> : (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="membership-plans">
            {(data?.plans || []).map((p) => (
              <div key={p.lookup_key} data-testid={`plan-${p.lookup_key}`} className="bg-[#121212] border border-white/10 p-6 flex flex-col hover:border-white/30 transition-colors">
                {p.founding && <span className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-black bg-white px-2 py-0.5 self-start mb-3">{data.founding_cap - data.founding_active} spots left</span>}
                <h3 className="font-display font-black uppercase tracking-tight text-xl">{p.name}</h3>
                <p className="font-mono-accent text-4xl font-bold mt-2 mb-1">${p.price}<span className="text-white/40 text-sm">/mo</span></p>
                <p className="text-white/40 text-xs mb-5">{p.sessions}</p>
                <ul className="space-y-2 mb-6 flex-1">
                  {p.features.map((f) => <li key={f} className="flex gap-2 text-sm text-white/70"><Check size={15} className="text-white/50 shrink-0 mt-0.5" />{f}</li>)}
                </ul>
                <button data-testid={`choose-${p.lookup_key}`} onClick={() => { setSelected(p); setError(""); }} className="inline-flex items-center justify-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white/85 transition-colors">Choose Plan <ArrowRight size={14} /></button>
              </div>
            ))}
            {data?.founding_full && (
              <div className="md:col-span-2 lg:col-span-4 border border-white/10 bg-[#0d0d0d] p-4 text-white/40 text-sm flex items-center gap-2" data-testid="founding-full-note"><Lock size={14} /> Founding Athlete memberships are currently full.</div>
            )}
          </div>
        )}

        <div className="mt-10 border border-white/10 bg-[#121212] p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div><p className="font-display font-bold uppercase text-lg">Not sure yet?</p><p className="text-white/50 text-sm">Start with a free speed evaluation or a one-time private session.</p></div>
          <div className="flex gap-3">
            <a href="/#evaluation" className="border border-white/20 px-5 py-3 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors">Free Evaluation</a>
            <a href="/book-evaluation" className="border border-white/20 px-5 py-3 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors">Private Session</a>
          </div>
        </div>
      </section>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/80 backdrop-blur-sm p-4 sm:p-8" data-testid="membership-signup-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) setSelected(null); }}>
          <form onSubmit={checkout} className="w-full max-w-md bg-[#0F0F0F] border border-white/15 my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h3 className="font-display font-bold uppercase tracking-tight text-lg">{selected.name} · ${selected.price}/mo</h3>
              <button type="button" onClick={() => setSelected(null)} data-testid="signup-close" className="text-white/40 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input data-testid="ms-first" required placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={inputCls} />
                <input data-testid="ms-last" required placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={inputCls} />
              </div>
              <input data-testid="ms-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
              <input data-testid="ms-sport" placeholder="Primary sport (optional)" value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} className={inputCls} />
              {error && <p data-testid="ms-error" className="text-sm text-red-400 border border-red-400/30 bg-red-400/5 px-4 py-3">{error}</p>}
              <button data-testid="ms-submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-6 py-4 hover:bg-white/85 transition-colors disabled:opacity-60">
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />} {submitting ? "Redirecting…" : "Continue to Payment"}
              </button>
              <p className="text-white/30 text-xs text-center">Secure checkout by Stripe · cancel anytime. Test card 4242 4242 4242 4242.</p>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
